import { analyzeTap, precomputeAnalysis, type Precomputed, type TapResult } from './chain';
import type { AnalysisSnapshot, RegionGrowOptions } from './types';

/**
 * Owns the analysis worker (or a main-thread fallback). The model snapshot is
 * sent once at load and pre-processed (weld/adjacency/normals) while the user
 * orbits; each tap then only sends a seed triangle, so results return in ms.
 * Falls back to running the same pure `chain` on the main thread if a worker
 * can't be created (e.g. an environment without module-worker support).
 */
export class AnalysisSession {
  private worker: Worker | null = null;
  private pre: Precomputed | null = null; // main-thread fallback state
  private requestCounter = 0;
  private pending = new Map<number, (r: TapResult | null) => void>();
  private precomputeResolve: (() => void) | null = null;
  private ready: Promise<void> = Promise.resolve();

  constructor(private opts: { useWorker?: boolean } = {}) {}

  /** Latest issued request id — callers compare against it to drop stale taps. */
  get latestRequestId(): number {
    return this.requestCounter;
  }

  /** Send the model snapshot and (in the worker, while the user orbits) pre-process
   *  it. Resolves when ready; `analyze` also awaits this internally. */
  setSnapshot(snapshot: AnalysisSnapshot): Promise<void> {
    this.teardown();
    this.ready = this.setup(snapshot);
    return this.ready;
  }

  private async setup(snapshot: AnalysisSnapshot): Promise<void> {
    if (this.opts.useWorker !== false && typeof Worker !== 'undefined') {
      try {
        const mod = await import('./worker?worker&inline');
        this.worker = new mod.default();
        this.worker.onmessage = (e) => this.onMessage(e);
        // A worker-level crash (uncaught throw outside the message handler, OOM
        // kill, deserialization failure) never posts a reply — settle EVERYTHING
        // so no caller is left awaiting forever.
        this.worker.onerror = () => this.settleAll();
        this.worker.onmessageerror = () => this.settleAll();
      } catch {
        this.worker = null;
      }
    }

    if (this.worker) {
      const worker = this.worker;
      await new Promise<void>((resolve) => {
        this.precomputeResolve = resolve;
        worker.postMessage({ type: 'precompute', snapshot }, snapshotTransferables(snapshot));
      });
      return;
    }

    // Fallback: precompute synchronously on the main thread.
    this.pre = precomputeAnalysis(snapshot);
  }

  /** Returns { id, result }; `id` lets the caller ignore superseded taps. */
  async analyze(seed: number, opts?: RegionGrowOptions): Promise<{ id: number; result: TapResult | null }> {
    await this.ready; // ensure precompute finished (avoids racing a fast tap after load)
    const id = ++this.requestCounter;
    if (this.worker) {
      const worker = this.worker;
      const result = await new Promise<TapResult | null>((resolve) => {
        this.pending.set(id, resolve);
        worker.postMessage({ type: 'analyze', requestId: id, seed, opts });
      });
      return { id, result };
    }
    // Yield a microtask so the immediate pulse can paint first.
    await Promise.resolve();
    return { id, result: this.pre ? analyzeTap(this.pre, seed, opts) : null };
  }

  private onMessage(e: MessageEvent) {
    const msg = e.data;
    if (msg.type === 'precomputed') {
      // On a precompute error the worker still posts (with `error`) — resolve either
      // way; failed precompute means analyze() falls to the worker's null-result path.
      this.precomputeResolve?.();
      this.precomputeResolve = null;
    } else if (msg.type === 'result') {
      const cb = this.pending.get(msg.requestId);
      if (cb) {
        this.pending.delete(msg.requestId);
        cb(msg.result ?? null);
      }
    }
  }

  /** Settle every outstanding promise (worker crashed / torn down) — callers get a
   *  null result (→ the app's detect-rejected path) rather than hanging forever. */
  private settleAll() {
    this.precomputeResolve?.();
    this.precomputeResolve = null;
    this.pending.forEach((cb) => cb(null));
    this.pending.clear();
  }

  private teardown() {
    this.worker?.terminate();
    this.worker = null;
    this.pre = null;
    this.settleAll();
  }

  dispose() {
    this.teardown();
  }
}

function snapshotTransferables(s: AnalysisSnapshot): Transferable[] {
  return [
    s.positions.buffer,
    s.indices.buffer,
    s.uvs.buffer,
    s.triMeshIndex.buffer,
    s.meshTriOffset.buffer,
    s.meshVertexOffset.buffer,
  ];
}
